/**
 * KinsCircle Phase 6D — Memory Spotlight Card.
 *
 * STABILITY.md §13–§14:
 *   - READ-ONLY surface that elevates a single standout memory from the
 *     existing semantic index into a "spotlight" hero — using only the
 *     user's own caption, tagged people, and date.
 *   - Renders null when `memorySpotlight` flag is OFF or no qualifying
 *     memory exists. Never auto-rotates without an explicit user click.
 *   - Anchored to a single sourceMemoryId. Citation is mandatory.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Star,
  Calendar,
  Users,
  RefreshCw,
  PlayCircle,
} from 'lucide-react';
import { isMemorySpotlightEnabled, isStoryPlaybackEnabled } from '@/lib/ai/featureFlag';
import { readIndexedMemories } from '@/lib/ai/semanticIndexer';
import {
  buildLegacyTimeline,
  type LegacyTimeline,
} from '@/lib/ai/legacyTimelineEngine';
import StoryPlaybackModal, { type PlaybackStory } from './StoryPlaybackModal';

const TONE_GRADIENTS = [
  'from-amber-100 via-pink-100 to-fuchsia-200',
  'from-rose-100 via-fuchsia-100 to-purple-200',
  'from-sky-100 via-cyan-100 to-emerald-100',
  'from-orange-100 via-amber-100 to-yellow-100',
  'from-indigo-100 via-violet-100 to-fuchsia-200',
];

const MemorySpotlightCard: React.FC = () => {
  if (!isMemorySpotlightEnabled()) return null;

  const all = useMemo(() => readIndexedMemories(), []);
  // Pick spotlight: prefer entries with the most-tagged people *and* a year.
  const ranked = useMemo(() => {
    return [...all]
      .map((m) => ({
        m,
        score:
          (m.row.taggedNames?.length ?? 0) * 2 +
          (m.vector.year ? 1 : 0) +
          ((m.row.caption?.length ?? 0) > 60 ? 1 : 0),
      }))
      .sort((a, b) => b.score - a.score);
  }, [all]);

  const [cursor, setCursor] = useState(0);
  const [playOpen, setPlayOpen] = useState(false);

  if (!ranked.length) return null;

  const top = ranked[Math.min(cursor, ranked.length - 1)]?.m;
  if (!top) return null;

  const tone = TONE_GRADIENTS[cursor % TONE_GRADIENTS.length];
  const tagged = top.row.taggedNames ?? [];
  const yearStr = top.vector.year ? `${top.vector.year}` : 'an undated moment';

  // Build a mini story (this memory + up to 5 semantically related ones)
  // for optional playback. Anchored to real source ids only.
  const story = useMemo<PlaybackStory | null>(() => {
    if (!isStoryPlaybackEnabled()) return null;
    const seed = top.row.id;
    const t: LegacyTimeline | null = buildLegacyTimeline(
      [{ row: top.row, year: top.vector.year }, ...all
        .filter((m) => m.row.id !== seed)
        .slice(0, 5)
        .map((m) => ({ row: m.row, year: m.vector.year }))],
      { maxSlidesPerTimeline: 6 },
    );
    if (!t) return null;
    return {
      label: top.row.title?.trim() || 'Featured Family Memory',
      subtitle: t.yearRange.from && t.yearRange.to
        ? `Based on ${t.memoryCount} memories from ${t.yearRange.from}–${t.yearRange.to}`
        : `Based on ${t.memoryCount} memor${t.memoryCount === 1 ? 'y' : 'ies'}`,
      slides: t.slides,
    };
  }, [top, all]);

  return (
    <section className="py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-5 h-5 text-amber-500" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Memory Spotlight
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
            Beta
          </span>
        </div>

        <Card className="overflow-hidden border-0 shadow-lg">
          <div
            className={`bg-gradient-to-br ${tone} p-6 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center`}
          >
            <div className="space-y-3">
              {top.vector.year && (
                <Badge className="bg-white/70 text-slate-800 border border-white/60 hover:bg-white/70">
                  <Calendar className="w-3.5 h-3.5 mr-1" />
                  {yearStr}
                </Badge>
              )}
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                {top.row.title?.trim() ||
                  (top.row.caption?.split(/[.!?]\s+/)[0] ?? 'A family moment').slice(
                    0,
                    80,
                  )}
              </h3>
              <p className="text-slate-800/90 leading-relaxed whitespace-pre-wrap">
                {(top.row.caption ?? '').slice(0, 280) ||
                  'A memory you saved with your family.'}
              </p>
              {tagged.length > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-slate-800/80">
                  <Users className="w-4 h-4" />
                  <span className="truncate">
                    {tagged.slice(0, 4).join(', ')}
                    {tagged.length > 4 && ` +${tagged.length - 4} more`}
                  </span>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCursor((c) => (c + 1) % ranked.length)}
                  className="bg-white/80 hover:bg-white text-slate-800 border border-white/60"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Show another
                </Button>
                {isStoryPlaybackEnabled() && story && (
                  <Button
                    size="sm"
                    onClick={() => setPlayOpen(true)}
                    className="bg-slate-900/90 hover:bg-slate-900 text-white"
                  >
                    <PlayCircle className="w-4 h-4 mr-1.5" />
                    Play this story
                  </Button>
                )}
              </div>
            </div>

            <Card className="hidden md:block w-44 h-44 bg-white/70 border-white/60 shadow-inner">
              <CardContent className="p-0 w-full h-full flex items-center justify-center">
                <Star className="w-12 h-12 text-amber-400" />
              </CardContent>
            </Card>
          </div>

          <div className="bg-white px-6 md:px-10 py-3 text-[11px] italic text-slate-500 border-t border-slate-100">
            Based on 1 memory from {yearStr}.
          </div>
        </Card>

        <p className="text-[11px] text-slate-400 mt-3">
          Spotlight rotates only when you tap "Show another" — never automatically.
        </p>
      </div>

      <StoryPlaybackModal
        open={playOpen}
        onOpenChange={setPlayOpen}
        story={story}
      />
    </section>
  );
};

export default MemorySpotlightCard;
